import { NameObjectiveObject } from '@components/EntityDrawers/constants/empty-objects';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import { TransformContext } from '@components/EntityDrawers/hooks/useTransformContext';
import { EntityValidationStatus } from '@constants/canvas/layers';
import {
  MaxDescriptionLength,
  NameObjectivesYup,
  YupCriteriaField,
  YupDayJs,
  YupMaturity,
  YupString,
  YupTargetMaturity,
  YupTimelineStartDate,
  YupValidationStatus,
} from '@constants/validation';
import { NameObjective, NodeDTO } from '@store/services/nodes/types';
import { parseCiaCriteria, stringToDayjs } from '@utils/drawers';
import {
  getEntitiesArray,
  transformArrayToOptions,
  withId,
} from '@utils/helpers';
import dayjs from 'dayjs';
import * as yup from 'yup';

export const commonYups = {
  [CommonKeys.Name]: YupString({ allowParagraph: true }).required(
    'Entity name is required',
  ),
  [CommonKeys.Owners]: yup.array().of(yup.object()),
  [CommonKeys.Domains]: yup.array().of(yup.object()),
  [CommonKeys.Description]: YupString({
    min: 1,
    max: MaxDescriptionLength,
    allowParagraph: true,
  }),
  [CommonKeys.ValidationStatus]: YupValidationStatus(),
  [CommonKeys.DateCreated]: YupDayJs().required(),
  [CommonKeys.DateReviewed]: YupDayJs().nullable(),
  [CommonKeys.QnousRef]: yup.array().of(yup.object()),
  [CommonKeys.NistRef]: yup.array().of(yup.object()),
  [CommonKeys.IsoRef]: yup.array().of(yup.object()),
  [CommonKeys.IsmRef]: yup.array().of(yup.object()),
  [CommonKeys.CisRef]: yup.array().of(yup.object()),
  [CommonKeys.OperationalStatus]: yup.string().nullable(),
  [CommonKeys.TimelineStartDate]: YupTimelineStartDate(),
  [CommonKeys.TimelineEndDate]: YupDayJs().nullable(),
  [CommonKeys.Metrics]: NameObjectivesYup(),
  [CommonKeys.CurrentMaturity]: YupMaturity(),
  [CommonKeys.TargetMaturity]: YupTargetMaturity(),
  [CommonKeys.OperationalModel]: YupString({ allowParagraph: true }),
  [CommonKeys.Partners]: yup.array().of(yup.object()),
  [CommonKeys.OperationalEffectiveness]: YupString({ allowParagraph: true }),
  [CommonKeys.DesignEffectiveness]: YupString({ allowParagraph: true }),
  [CommonKeys.Assurance]: YupCriteriaField,
  [CommonKeys.Coverage]: YupCriteriaField,
  [CommonKeys.Applicability]: YupCriteriaField,
  [CommonKeys.IsReported]: yup.boolean(),
  [CommonKeys.OwnersToDelete]: yup.array().of(yup.string()),
};

export const commonDefaults = {
  [CommonKeys.Name]: '',
  [CommonKeys.Owners]: [],
  [CommonKeys.Domains]: [],
  [CommonKeys.Description]: '',
  [CommonKeys.ValidationStatus]: EntityValidationStatus.Draft,
  [CommonKeys.DateReviewed]: null,
  [CommonKeys.DateCreated]: dayjs(),
  [CommonKeys.QnousRef]: [],
  [CommonKeys.NistRef]: [],
  [CommonKeys.IsoRef]: [],
  [CommonKeys.IsmRef]: [],
  [CommonKeys.CisRef]: [],
  [CommonKeys.OperationalStatus]: null,
  [CommonKeys.TimelineStartDate]: null,
  [CommonKeys.TimelineEndDate]: null,
  [CommonKeys.Metrics]: [withId(NameObjectiveObject)],
  [CommonKeys.CurrentMaturity]: null,
  [CommonKeys.TargetMaturity]: null,
  [CommonKeys.OperationalModel]: '',
  [CommonKeys.Partners]: [],
  [CommonKeys.OperationalEffectiveness]: '',
  [CommonKeys.DesignEffectiveness]: '',
  [CommonKeys.Assurance]: {},
  [CommonKeys.Coverage]: {},
  [CommonKeys.Applicability]: {},
  [CommonKeys.IsReported]: true,
  [CommonKeys.OwnersToDelete]: [],
};

export const commonTransformers: Record<
  CommonKeys,
  (node: NodeDTO<any>, context: TransformContext) => any
> = {
  [CommonKeys.Name]: ({ name }) => name,
  [CommonKeys.Owners]: ({ requirement_owner }, { settings }) =>
    transformArrayToOptions(
      requirement_owner?.filter(
        (nodeOwner) => settings.requirement_owners?.includes(nodeOwner),
      ) ?? [],
    ),
  [CommonKeys.Domains]: ({ domainNodes }, { domains: projectDomains }) => {
    const directDomains =
      domainNodes
        ?.filter(({ type }) => type === 'direct')
        ?.map(({ domain_id }) => {
          const projectDomain = projectDomains?.find(
            ({ id }) => id === domain_id,
          )?.name;

          return {
            value: domain_id ?? '',
            label: projectDomain ?? domain_id ?? '',
          };
        }) ?? [];

    return directDomains;
  },
  [CommonKeys.Description]: ({ description }) => description ?? '',
  [CommonKeys.ValidationStatus]: ({ valid_status }) => valid_status,
  [CommonKeys.DateCreated]: ({ created_at }) => dayjs(created_at),
  [CommonKeys.DateReviewed]: ({ reviewed_at }) =>
    reviewed_at ? dayjs(reviewed_at) : null,
  [CommonKeys.QnousRef]: ({ qnous_ref }, { qnousMap }) => {
    return (
      qnous_ref?.map((id) => ({
        value: id,
        label: qnousMap.get(id) ?? '',
      })) ?? []
    );
  },
  [CommonKeys.NistRef]: ({ nist }, { nistMap }) => {
    return (
      nist?.map((id) => ({
        value: id,
        label: nistMap.get(id) ?? '',
      })) ?? []
    );
  },
  [CommonKeys.IsoRef]: ({ iso }, { isoMap }) => {
    return (
      iso?.map((id) => ({
        value: id,
        label: isoMap.get(id) ?? '',
      })) ?? []
    );
  },
  [CommonKeys.IsmRef]: ({ ism }, { ismMap }) => {
    return (
      ism?.map((id) => ({
        value: id,
        label: ismMap.get(id) ?? '',
      })) ?? []
    );
  },
  [CommonKeys.CisRef]: ({ cis }, { cisMap }) => {
    return (
      cis?.map((id) => ({
        value: id,
        label: cisMap.get(id) ?? '',
      })) ?? []
    );
  },
  [CommonKeys.OperationalStatus]: ({ meta_data: { operational_status } }) =>
    operational_status ?? '',
  [CommonKeys.TimelineStartDate]: ({ meta_data: { start_date } }) =>
    stringToDayjs(start_date),
  [CommonKeys.TimelineEndDate]: ({ meta_data: { end_date } }) =>
    stringToDayjs(end_date),
  [CommonKeys.Metrics]: ({ meta_data: { metric } }) =>
    getEntitiesArray<NameObjective>(metric, NameObjectiveObject),
  [CommonKeys.CurrentMaturity]: ({ meta_data: { current_maturity } }) =>
    current_maturity ?? '',
  [CommonKeys.TargetMaturity]: ({ meta_data: { target_maturity } }) =>
    target_maturity ?? '',
  [CommonKeys.OperationalModel]: ({ meta_data: { operational_model } }) =>
    operational_model ?? '',
  [CommonKeys.Partners]: ({ partners }) =>
    transformArrayToOptions(partners ?? []),
  [CommonKeys.OperationalEffectiveness]: ({
    meta_data: { operational_effectiveness },
  }) => operational_effectiveness ?? '',
  [CommonKeys.DesignEffectiveness]: ({ meta_data: { design_effectiveness } }) =>
    design_effectiveness ?? '',
  [CommonKeys.Assurance]: ({ cia }, { ciaSettings }) =>
    parseCiaCriteria(
      cia?.assurance_level,
      ciaSettings.assurance_level.criterias,
    ),
  [CommonKeys.Coverage]: ({ cia }, { ciaSettings }) =>
    parseCiaCriteria(cia?.coverage, ciaSettings.coverage.criterias),
  [CommonKeys.Applicability]: ({ cia }, { ciaSettings }) =>
    parseCiaCriteria(cia?.applicability, ciaSettings.applicability.criterias),
  [CommonKeys.IsReported]: ({ is_reported }) => is_reported ?? true,
  [CommonKeys.OwnersToDelete]: () => [],
};
